/**
 * TypeScript types for the modeling system.
 * Mirrors backend models: enums.py, data_artifact.py, model_definition.py, model_run.py
 */

// =============================================================================
// Enums (literal unions)
// =============================================================================

export type ModelingNodeType = 'input_data' | 'parameter' | 'transform' | 'calculate' | 'validate' | 'output';
export type EngineType = 'google_sheets' | 'python' | 'jupyter';
export type ModelArtifactType = 'table' | 'scalar' | 'matrix' | 'time_series';
export type ColumnType = 'string' | 'number' | 'boolean' | 'date' | 'enum';
export type ModelStatus = 'draft' | 'active' | 'archived';
export type RunStatus = 'pending' | 'running' | 'completed' | 'failed';
export type ValidationSeverity = 'error' | 'warning';
export type InputSourceType = 'file_upload' | 'database' | 'api' | 'model_output' | 'manual_entry';

// =============================================================================
// Build phase (PhaseStepper)
// =============================================================================

export type BuildPhase = 'plan' | 'institution_context' | 'requirements' | 'build' | 'run' | 'documentation' | 'output_package';

// =============================================================================
// Data Artifact Schemas
// =============================================================================

export interface ColumnDefinition {
  name: string;
  column_type: ColumnType;
  required: boolean;
  description?: string;
  allowed_values?: string[];
}

export interface TableSchema {
  artifact_type: 'table';
  columns: ColumnDefinition[];
}

export interface ScalarSchema {
  artifact_type: 'scalar';
  value_type: ColumnType;
  unit?: string;
  allowed_values?: string[];
}

export interface MatrixSchema {
  artifact_type: 'matrix';
  row_dimension: string;
  col_dimension: string;
  cell_type: ColumnType;
  unit?: string;
}

export interface TimeSeriesSchema {
  artifact_type: 'time_series';
  frequency: string;
  time_column: string;
  value_columns: ColumnDefinition[];
}

export type DataArtifactSchema = TableSchema | ScalarSchema | MatrixSchema | TimeSeriesSchema;

export interface DataArtifact {
  artifact_name: string;
  artifact_type: ModelArtifactType;
  artifact_schema: DataArtifactSchema;
  description?: string;
}

// =============================================================================
// Model Definition (value-free shell)
// =============================================================================

export interface NodeInput {
  artifact_name: string;
  source_node_id: string;
  expected_type: ModelArtifactType;
  required: boolean;
}

export interface NodeOutput {
  artifact: DataArtifact;
}

// Structured VALIDATE-node contract. `claim` is the regulator-facing
// assertion; `citation` anchors it to a regulatory reference.
export interface ValidatesThat {
  claim: string;
  citation?: string;
}

// Cross-workspace methodology grounding (Addition 2 of build-projects).
// Typed pointer from a production node to a research finding.
export interface FindingRef {
  workspace_id: string;
  finding_id: string;
  citation_excerpt?: string;
}

// Typed pointer from a model node to an upstream SpecRequirement —
// "this node implements REQ-NNN". Mirrors backend RequirementRef
// (models/spec_requirement.py). Identity is ALWAYS the composite
// {workspace_id, requirement_id}; two specs can both author REQ-001
// without colliding. Replaces the spec-REQ use of the legacy free-text
// handoff_item_ids (kept readable only).
export interface RequirementRef {
  workspace_id: string;
  requirement_id: string;
  impl_note?: string;
}

// NodeArtifacts (Addition 3a). Content is opaque on the wire — the
// frontend doesn't render the templates yet but must preserve them
// through round-trips.
export type NodeArtifacts = Record<string, unknown>;

// One review row stamped onto a node by validate_work. Opaque
// shape on the wire — we only need its presence to infer
// "validate_work has run" + verdict for the deliverable checklist.
export interface NodeReview {
  review_id?: string;
  session_id?: string;
  reviewer_type?: string;
  reviewer_id?: string;
  verdict?: 'approved' | 'flagged' | 'rejected';
  rationale?: string;
  [k: string]: unknown;
}

export type NodeValidationStatus = 'approved' | 'flagged' | 'rejected' | 'review_needed' | 'unknown';

export interface NodeDefinition {
  node_id: string;
  node_type: ModelingNodeType;
  cluster_id: string | null;
  name: string;
  description?: string;
  engine?: EngineType;
  inputs: NodeInput[];
  outputs: NodeOutput[];
  logic_description?: string;
  logic_steps: string[];
  // VALIDATE-node contract — structured {claim, citation}. Surfaced
  // in the run report and node detail panels.
  validates_that?: ValidatesThat;
  source_type?: InputSourceType;
  handoff_item_ids: string[];
  component_id?: string;
  position?: { x: number; y: number };
  metadata: Record<string, unknown>;
  // Build-project additions — backward-compat optional.
  // Typed REQ links: upstream SpecRequirements this node implements.
  // Arrives verbatim from the backend NodeDefinition (model_dump) via
  // both read paths (workspace-state models map and GET /models/{id})
  // — there is no field-mapping deserializer to update.
  requirement_refs?: RequirementRef[];
  methodology_finding_refs?: FindingRef[];
  node_artifacts?: NodeArtifacts;
  // Review state stamped by validate_work (PR-A handoff doc, Reviewable
  // contract). Both fields are absent on a freshly built node.
  reviews?: NodeReview[];
  validation_status?: NodeValidationStatus;
}

export interface NodeConnection {
  connection_id: string;
  from_node_id: string;
  to_node_id: string;
  artifact_name: string;
}

// Institution-wide template instances (Addition 3a). Opaque on the
// wire — the frontend round-trips them; rendering lives in a later UI
// slice.
export type ModelTemplate = Record<string, unknown>;

export interface ModelDefinition {
  model_id: string;
  name: string;
  description?: string;
  version: number;
  handoff_id?: string;
  nodes: NodeDefinition[];
  connections: NodeConnection[];
  project_id?: string;
  user_id?: string;
  status: ModelStatus;
  created_at: string;
  updated_at: string;
  // Build-project additions — backward-compat optional.
  supersedes_model_id?: string;
  charter?: ModelTemplate;
  tier?: ModelTemplate;
  stakeholders?: ModelTemplate;
  regulatory_references?: ModelTemplate;
  data_inventory?: ModelTemplate;
  assumptions_register?: ModelTemplate;
  methodology_rationale?: ModelTemplate;
  validation_plan?: ModelTemplate;
  monitoring_metrics?: ModelTemplate;
  change_management_policy?: ModelTemplate;
  limitations_register?: ModelTemplate;
  override_mechanism?: ModelTemplate;
  validation_outcome?: ModelTemplate;
}

// =============================================================================
// Model Run (execution record)
// =============================================================================

export interface NodeResult {
  node_id: string;
  node_name: string;
  status: RunStatus;
  execution_time_ms?: number;
  engine_metadata: Record<string, unknown>;
  artifact_paths?: Record<string, string>;
  error?: string;
  error_detail?: string;
  started_at?: string;
  completed_at?: string;
}

export interface ValidationResult {
  check_id: string;
  check_name: string;
  passed: boolean;
  details: string;
  expected_value?: string;
  actual_value?: string;
  source_ids: string[];
  severity: ValidationSeverity;
}

export interface ParameterSnapshot {
  parameter_node_id: string;
  parameter_name: string;
  value: unknown;
  source?: string;
}

export interface ModelRun {
  run_id: string;
  model_id: string;
  model_version: number;
  project_id?: string;
  user_id?: string;
  status: RunStatus;
  parameters: ParameterSnapshot[];
  node_results: NodeResult[];
  validation_results: ValidationResult[];
  triggered_by?: string;
  execution_time_ms?: number;
  error?: string;
  dashboard_status?: 'running' | 'completed' | 'failed' | null;
  dashboard_error?: string | null;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

// =============================================================================
// Summary types (for list endpoints)
// =============================================================================

export interface ModelSummary {
  model_id: string;
  name: string;
  status: ModelStatus;
  version: number;
  node_count: number;
  connection_count: number;
  created_at: string;
  updated_at: string;
}

export interface ModelRunSummary {
  run_id: string;
  model_id: string;
  status: RunStatus;
  triggered_by?: string;
  execution_time_ms?: number;
  created_at: string;
}

// =============================================================================
// Discriminated engine metadata types
// =============================================================================

export interface SheetsNodeMetadata {
  engine: 'google_sheets';
  spreadsheet_url?: string;
  sheet_id?: string;
  input_sheet?: string;
  output_sheet?: string;
  input_gid?: string;
  output_gid?: string;
}

export interface PythonNodeMetadata {
  engine: 'python';
  compute_endpoint?: string;
  script_path?: string;
  description?: string;
}

export interface JupyterNodeMetadata {
  engine: 'jupyter';
  notebook_url?: string;
}

export type NodeMetadata = SheetsNodeMetadata | PythonNodeMetadata | JupyterNodeMetadata;

// =============================================================================
// Type guards
// =============================================================================

export function isSheetsNode(node: NodeDefinition): node is NodeDefinition & { engine: 'google_sheets'; metadata: SheetsNodeMetadata } {
  return node.engine === 'google_sheets';
}

export function isPythonNode(node: NodeDefinition): node is NodeDefinition & { engine: 'python'; metadata: PythonNodeMetadata } {
  return node.engine === 'python';
}

export function isJupyterNode(node: NodeDefinition): node is NodeDefinition & { engine: 'jupyter'; metadata: JupyterNodeMetadata } {
  return node.engine === 'jupyter';
}

// =============================================================================
// Visual helpers
// =============================================================================

const NODE_TYPE_COLORS: Record<ModelingNodeType, string> = {
  input_data: 'var(--node-category-input-data)',
  parameter: 'var(--node-category-parameter)',
  transform: 'var(--node-category-transform)',
  calculate: 'var(--node-category-calculate)',
  validate: 'var(--node-category-validate)',
  output: 'var(--node-category-output)',
};

const NODE_TYPE_LABELS: Record<ModelingNodeType, string> = {
  input_data: 'Input Data',
  parameter: 'Parameter',
  transform: 'Transform',
  calculate: 'Calculate',
  validate: 'Validate',
  output: 'Output',
};

const ENGINE_LABELS: Record<EngineType, string> = {
  google_sheets: 'Google Sheets',
  python: 'Python',
  jupyter: 'Jupyter',
};

export function getNodeTypeColor(type: ModelingNodeType): string {
  return NODE_TYPE_COLORS[type] ?? 'var(--fg-tertiary)';
}

/**
 * Resolve a `var(--token)` reference to its computed value at runtime.
 * Needed for SVG `fill` attribute consumers (e.g. React Flow MiniMap)
 * where the browser does not resolve CSS variables.
 */
export function resolveCssVar(varRef: string): string {
  if (typeof window === 'undefined') return varRef;
  const match = varRef.match(/var\((--[a-z0-9-]+)\)/i);
  if (!match) return varRef;
  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || varRef;
}

export function getNodeTypeColorResolved(type: ModelingNodeType): string {
  return resolveCssVar(NODE_TYPE_COLORS[type] ?? 'var(--fg-tertiary)');
}

export function getNodeTypeLabel(type: ModelingNodeType): string {
  return NODE_TYPE_LABELS[type] ?? type;
}

export function getEngineLabel(engine: EngineType): string {
  return ENGINE_LABELS[engine] ?? engine;
}

// =============================================================================
// URL helpers
// =============================================================================

/**
 * Build an embeddable Google Sheets URL.
 * Parameter ordering: ?embedded=true first, then &range= if provided, then #gid= fragment last.
 */
export function buildSheetEmbedUrl(spreadsheetUrl: string, gid?: string, range?: string): string {
  // Extract spreadsheet ID from URL
  const match = spreadsheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  if (!match) return spreadsheetUrl;

  let url = `https://docs.google.com/spreadsheets/d/${match[1]}/edit?embedded=true`;
  if (range) {
    url += `&range=${encodeURIComponent(range)}`;
  }
  if (gid) {
    url += `#gid=${gid}`;
  }
  return url;
}
